import { useState, useEffect } from "react";
import T from "../theme";
import Stars from "../components/Stars";

function ComputerIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 160 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#93c5fd"/>
          <stop offset="100%" stopColor="#1d4ed8"/>
        </linearGradient>
        <filter id="glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Orbital rings */}
      <ellipse cx="80" cy="68" rx="68" ry="22" stroke="#3b82f6" strokeWidth="1.6" fill="none" opacity="0.5"
        style={{ animation: "spin1 7s linear infinite", transformOrigin: "80px 68px" }}/>
      <ellipse cx="80" cy="68" rx="68" ry="22" stroke="#93c5fd" strokeWidth="1" fill="none" opacity="0.28"
        style={{ animation: "spin2 11s linear infinite", transformOrigin: "80px 68px" }}/>

      {/* Monitor body */}
      <rect x="14" y="30" width="132" height="90" rx="8" fill="rgba(4,13,35,0.95)" stroke="url(#sg)" strokeWidth="2" filter="url(#glow)"/>
      <rect x="24" y="40" width="112" height="68" rx="5" fill="rgba(59,130,246,0.07)"/>

      {/* Code lines on screen */}
      <rect x="36" y="55" width="50" height="5" rx="2.5" fill="#93c5fd" opacity="0.9"/>
      <rect x="42" y="66" width="68" height="5" rx="2.5" fill="#3b82f6" opacity="0.7"/>
      <rect x="42" y="77" width="54" height="5" rx="2.5" fill="#3b82f6" opacity="0.55"/>
      <rect x="36" y="88" width="36" height="5" rx="2.5" fill="#93c5fd" opacity="0.8"/>
      <rect x="76" y="88" width="10" height="5" rx="2" fill="white" opacity="0.9"
        style={{ animation: "blink 1.2s step-end infinite" }}/>

      {/* Neck + base */}
      <rect x="74" y="120" width="12" height="20" rx="3" fill="#3b82f6" opacity="0.55"/>
      <rect x="46" y="138" width="68" height="12" rx="6" fill="url(#sg)" opacity="0.85"/>

      {/* Gear top-left */}
      <g transform="translate(8, 6)" filter="url(#glow)">
        <circle cx="14" cy="14" r="7" fill="none" stroke="#93c5fd" strokeWidth="2"/>
        <circle cx="14" cy="14" r="3.5" fill="#3b82f6"/>
        {[0,45,90,135,180,225,270,315].map((a,i) => (
          <rect key={i} x="12.8" y="2" width="2.4" height="5" rx="1.2" fill="#93c5fd"
            transform={`rotate(${a} 14 14)`} opacity="0.85"/>
        ))}
      </g>

      <style>{`
        @keyframes spin1 { from{transform:rotate(0deg) rotateX(72deg)} to{transform:rotate(360deg) rotateX(72deg)} }
        @keyframes spin2 { from{transform:rotate(0deg) rotateX(60deg)} to{transform:rotate(-360deg) rotateX(60deg)} }
        @keyframes blink  { 0%,100%{opacity:0.9} 50%{opacity:0} }
      `}</style>
    </svg>
  );
}

export default function HomeSection() {
  const [on, setOn] = useState(false);
  useEffect(() => { setTimeout(() => setOn(true), 80); }, []);

  const fade = (d = 0) => ({
    opacity: on ? 1 : 0,
    transform: on ? "none" : "translateY(28px)",
    transition: `opacity 0.85s ease ${d}s, transform 0.85s ease ${d}s`,
  });

  return (
    <section style={{
      minHeight: "100vh", position: "relative", overflow: "hidden",
      background: `radial-gradient(ellipse 80% 70% at 15% 55%, rgba(20,60,200,0.28) 0%, #040d1a 60%),
                   radial-gradient(ellipse 50% 60% at 88% 15%, rgba(15,70,220,0.14) 0%, transparent 60%)`,
      display: "flex", flexDirection: "column", justifyContent: "center",
    }}>
      <Stars />

      {/* Orb blobs */}
      <div style={{
        position: "absolute", left: -160, top: "10%", width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(20,60,200,0.2) 0%, transparent 70%)",
        filter: "blur(60px)", pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", right: -100, top: "5%", width: 500, height: 700, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(15,45,180,0.14) 0%, transparent 70%)",
        filter: "blur(70px)", pointerEvents: "none",
      }} />

      {/* Subtle grid lines */}
      {[20, 40, 60, 80].map((p) => (
        <div key={p} style={{
          position: "absolute", top: 0, bottom: 0, left: `${p}%`, width: 1,
          background: `linear-gradient(to bottom, transparent, rgba(59,130,246,0.05) 25%, rgba(59,130,246,0.05) 75%, transparent)`,
          pointerEvents: "none",
        }} />
      ))}

      {/* MAIN CONTENT */}
      <div style={{
        position: "relative", zIndex: 2,
        padding: "80px 48px 48px",
        width: "100%",
        maxWidth: 1400,
        margin: "0 auto",
      }}>

        {/* School badge */}
        <div style={{ ...fade(0.05), marginBottom: 40 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            border: "1px solid rgba(59,130,246,0.35)", borderRadius: 20,
            padding: "6px 18px",
            fontFamily: T.mono, fontSize: 11, color: T.gray2, letterSpacing: 2,
            background: "rgba(59,130,246,0.06)",
          }}>
            POTISARNPITTAYAKORN SCHOOL
          </div>
        </div>

        {/* TITLE BLOCK — icon left, text stacked right */}
        <div style={{ ...fade(0.1), display: "flex", alignItems: "flex-start", gap: "clamp(16px, 2.5vw, 48px)" }}>

          {/* Icon column */}
          <div style={{
            flexShrink: 0,
            width: "clamp(80px, 9vw, 160px)",
            marginTop: "0.1em",
          }}>
            <ComputerIcon />
          </div>

          {/* Text column — fills remaining width */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Row 1: SCI-COM */}
            <div style={{
              fontFamily: T.sans,
              fontSize: "clamp(52px, 9.5vw, 160px)",
              fontWeight: 900,
              color: T.white,
              letterSpacing: "-0.04em",
              lineHeight: 0.9,
              whiteSpace: "nowrap",
            }}>SCI-COM</div>

            {/* Row 2: COMPUTER */}
            <div style={{
              fontFamily: T.sans,
              fontSize: "clamp(52px, 9.5vw, 160px)",
              fontWeight: 900,
              color: T.blue,
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
            }}>COMPUTER</div>

            {/* Row 3: SCIENCE 2026 */}
            <div style={{
              fontFamily: T.sans,
              fontSize: "clamp(52px, 9.5vw, 160px)",
              fontWeight: 900,
              color: T.white,
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
            }}>
              SCIENCE{" "}
              <span style={{ color: T.blue }}>2026</span>
            </div>
          </div>
        </div>

        {/* Subtitle + buttons */}
        <div style={{ ...fade(0.3), marginTop: 48 }}>
          <p style={{
            color: T.gray2, fontSize: "clamp(13px, 1.3vw, 16px)",
            lineHeight: 1.75, marginBottom: 32, maxWidth: 480,
          }}>
            Where bold ideas meet breakthrough technology.<br />
            Join the next wave of digital innovation.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <button style={{
              background: "rgba(5,20,55,0.75)",
              border: "1.5px solid rgba(59,130,246,0.5)",
              color: T.gray1,
              fontFamily: T.sans, fontWeight: 700, fontSize: 13,
              padding: "13px 32px", borderRadius: 26, cursor: "not-allowed",
              letterSpacing: 0.3,
            }}>Registration closed</button>
            <button style={{
              background: "transparent",
              border: `1px solid ${T.gray3}`,
              color: T.gray2,
              fontFamily: T.sans, fontSize: 13,
              padding: "13px 32px", borderRadius: 26, cursor: "pointer",
              letterSpacing: 0.3,
            }}>Learn More →</button>
          </div>
        </div>
      </div>
    </section>
  );
}
